import { v4 as uuidv4 } from 'uuid';

const httpMethods = {
   GET: 'get',
   POST: 'post',
   PUT: 'put',
   PATCH: 'patch',
   DELETE: 'delete',
};

const roleNames = {
   SITE_ADMIN: 'Site Admin',
   ORGANIZATION_ADMIN: 'Organization Admin',
   STANDARD_USER: 'Standard User',
};

const routePaths = {
   ACCOUNT_DISABLED: '/account/disabled',
   BOOKING: '/c/:organizationSlug/book',
   HOTEL_DETAIL_MAP_VIEW: '/c/:organizationSlug/hotels/:hotelId/map-view',
   HOTEL_DETAIL: '/c/:organizationSlug/hotels/:hotelId',
   HOTEL_GALLERY: '/c/:organizationSlug/hotels/:hotelId/gallery',
   HOTEL_MAP_VIEW: '/c/:organizationSlug/hotels/map-view',
   HOTEL_SEARCH: '/c/:organizationSlug/hotels',
   LANDING: '/',
   LOGIN_PAGE: '/login',
   LOGOUT_PAGE: '/logout',
   MY_ACCOUNT: '/my-account',
   PAYMENT_METHODS: '/payment-methods',
   MY_TRIP_DETAIL: '/my-trips/:tripId',
   MY_FAVORITES: '/my-favorites',
   MY_PRICE_TRACKING: '/my-price-tracking',
   MY_TRIPS: '/my-trips',
   NOT_FOUND_PAGE: '/not-found',
   PROFILE_PAGE: '/profile',
   REGISTER_PAGE: '/register',
   SIGNUP: '/sign-up',
   SIGNOUT: '/signout',
   ORGANIZATION: {
      LANDING: '/c',
      DETAILS: '/c/:organizationSlug',
   },
   EVENT: {
      LANDING: '/c/:organizationSlug/events/:eventSlug',
   },
   GAME_GUIDE: '/c/:organizationSlug/program/:programSlug/game/:gameId',
   PLAN_SEASON: '/c/:organizationSlug/plan',
   PLAN_SEASON_PROGRAM: '/c/:organizationSlug/plan/:programSlug',
   ADMIN: {
      INDEX: '/admin',
      ORGANIZATION: {
         INDEX: '/admin/organizations',
         DETAIL: '/admin/organizations/:id',
         ADD: '/admin/organizations/add',
         EDIT: '/admin/organizations/:id',
      },
      MANAGE_TRIP: {
         INDEX: '/admin/manage-trips',
         DETAIL: '/admin/manage-trips/:id',
      },
      DISPUTES: '/admin/disputes',
      // Team hotel autopick: the backlog of games with no team hotel.
      TEAM_HOTELS: '/admin/team-hotels',
      PROGRAM_SCHEDULE: {
         INDEX: '/admin/program-schedules',
         DETAIL: '/admin/program-schedules/:id',
         GAME_GUIDE: '/admin/program-schedules/:id/games/:gameId/guide',
      },
      USER: {
         INDEX: '/admin/users',
         DETAIL: '/admin/users/:id',
         ADD: '/admin/users/add',
         EDIT: '/admin/users/:id',
      },
      TRANSACTIONS: {
         INDEX: '/admin/transactions',
      },
      PROCEEDS_REPORT: {
         INDEX: '/admin/proceeds-report',
      },
      ROLE: {
         INDEX: '/admin/roles',
         DETAIL: '/admin/roles/:id',
      },
      COUPON: {
         INDEX: '/admin/coupon',
      },
      REVIEW: {
         INDEX: '/admin/review',
      },
      HOTEL_BLACKLIST: {
         INDEX: '/admin/hotel-blacklist',
      },
      SETTINGS: '/settings',
      SITE_CONFIG_REGISTRY: '/admin/site-config-registry',
      FONTS: '/admin/fonts',
      ACTIVITY_LOGS: '/admin/activity-logs',
      BOOKING_FAILURE_TRACKER: { INDEX: '/admin/booking-failure-tracker', DETAIL: '/admin/booking-failure-tracker/:id' },
      FRAUD_WATCH: { INDEX: '/admin/fraud-watch', DETAIL: '/admin/fraud-watch/:userId' },
      TOURIST_OVERRIDE: '/admin/tourist-override',
   },
   UNAUTHORIZED: '/unauthorized',
};

const status = {
   ACTIVE: 'Active',
   INACTIVE: 'Inactive',
};

/**
 * NPT-1649 — query params the destination map's click-through uses to say
 * "and land on this row": the schedule detail page reads GAME_ID to scroll +
 * focus a game, the program-schedule tabs page reads TAB + EVENT_ID to land
 * on the Events Library tab with a specific library event opened.
 */
export const DESTINATION_MAP_QUERY_PARAM = {
   GAME_ID: 'gameId',
   TAB: 'tab',
   EVENT_ID: 'eventId',
};

const apiStatus = {
   CREATED: 201,
   SUCCESS: 200,
   NO_CONTENT: 204,
};

const defaultPageCount = 15;

const exportFileName = {
   ORGANIZATION: 'ListOrganization.csv',
   USER: 'ListUser.csv',
   USER_ORGANIZATION: 'UserOrganization.csv',
   TOKENS: 'Tokens.csv',
   ROLE: 'Role.csv',
   MY_SUBSCRIPTIONS: 'MySubscriptions.csv',
};

const roles = ['admin', 'user', 'super-admin'];

const searchMaxLength = 500;

const delayShowToast = 2000;

const acceptImageTypeFile = '.png, .jpeg, .jpg, .gif';

const BUTTON_COLOR = {
   BLUE: 'bg-blue-600 hover:bg-blue-700',
   RED: 'bg-red-500 hover:bg-red-800',
   GRAY: 'bg-gray-300 hover:bg-gray-500',
   OUTLINE: 'bg-white border border-gray-200 hover:bg-blue-100 hover:border-blue-600',
};

const page_default = 0;
const limit_default = 1000;

const roleTypeOptions = [
   { label: 'Site', value: 'Site' },
   { label: 'Organization', value: 'Organization' },
];

const IMAGE_TYPE_REGEX = /\.(jpg|jpeg|png|gif)$/i;

const ORGANIZATION_KEY = 'organization_id';
const TOKEN_KEY = 'token';
const USER_KEY = 'user';
const WORKOS_USER_KEY = 'workos_user';
const RSVP_EVENT_KEY = 'rsvp_events';
const DEFAULT_GIVE_BACK_PERCENT = 50;

const permissionActions = {
   MANAGE: 'manage',
   READ: 'read',
   WRITE: 'write',
};

const permissionKeys = {
   PLATFORM_ADMIN: 'PLATFORM_ADMIN',
   READ_AUTHEN: 'READ_AUTHEN',
   WRITE_AUTHEN: 'WRITE_AUTHEN',
   READ_ORG: 'READ_ORG',
   WRITE_ORG: 'WRITE_ORG',
   READ_ROLE: 'READ_ROLE',
   WRITE_ROLE: 'WRITE_ROLE',
   READ_USER: 'READ_USER',
   WRITE_USER: 'WRITE_USER',
   EXPORT_ORG: 'EXPORT_ORG',
   IMPORT_ORG: 'IMPORT_ORG',
   READ_SCHEDULE: 'READ_SCHEDULE',
   WRITE_SCHEDULE: 'WRITE_SCHEDULE',
};

const permissionsSite = [
   permissionKeys.PLATFORM_ADMIN,
   permissionKeys.READ_ORG,
   permissionKeys.READ_USER,
   permissionKeys.READ_ROLE,
   permissionKeys.READ_AUTHEN,
   permissionKeys.WRITE_ORG,
   permissionKeys.WRITE_USER,
   permissionKeys.WRITE_ROLE,
   permissionKeys.WRITE_AUTHEN,
   permissionKeys.EXPORT_ORG,
   permissionKeys.IMPORT_ORG,
   permissionKeys.READ_SCHEDULE,
   permissionKeys.WRITE_SCHEDULE,
];

const permissionsOrg = [
   permissionKeys.PLATFORM_ADMIN,
   permissionKeys.READ_USER,
   permissionKeys.WRITE_USER,
   permissionKeys.READ_SCHEDULE,
   permissionKeys.WRITE_SCHEDULE,
];

const pageType = {
   SITE: 'Site',
   ORGANIZATION: 'Organization',
};

const pageSizeOptions = [
   { value: 15, label: 15 },
   { value: 30, label: 30 },
   { value: 45, label: 45 },
   { value: 50, label: 50 },
   { value: 65, label: 65 },
];

export const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const DATE_FORMAT = {
   DATE_TIME_12H_FORMAT: 'MMMM DD, YYYY hh:mm A',
   BOOKING_DATE_WITH_OUT_TIME: 'ddd, MMM D, YYYY',
   BOOKING_TIME: 'h:mm A',
   BOOKING_DATE: 'MMM D, YYYY',
   LOCALE_DATE_TIME: 'MMM D, YYYY HH:mm A [hotel local time]',
   FULL: 'll',
   PAYMENT_TIME: 'MMM D, YYYY hh:mm:ss A',
   STANDARD: 'MM/DD/YYYY',
   SERVER_DATE: 'YYYY-MM-DD',
   US_DATE: 'MM-DD-YYYY',
};

export const sorts = {
   RECOMMENDED: 'Recommended',
   BEST_RATING: 'Best rating',
   DISTANCE: 'Distance from search location',
};

export const colorDefault = {
   colorLightPrimary: '#E7F3F4',
   colorLightView: '#FAF5F0',
   colorPrimary: '#62A4AB',
   colorSecondary: '#E9A468',
   colorTertiary: '#457377',
   colorView: '#297242',
   colorCallToAction: '#4CAF50',
   colorHeaderText: '#000000',
   colorHeroText: '#000000',
   colorStartBackground: '#85C2C9',
   colorEndBackground: '#E7F3F4',
   colorHeroTextDetail: '#000000',
   colorStartBackgroundDetail: '#85C2C9',
   colorEndBackgroundDetail: '#E7F3F4',
};

export const taxesAndFees = {
   hotel_fee: 'Hotel fee',
   cleaning_fee: 'Cleaning fee',
   vat: 'VAT',
   resort_fee: 'Resort fee',
   luxury_tax: 'Luxury tax',
   electricity_fee: 'Electricity fee',
   transfer_fee: 'Transfer to hotel',
   environmental_fee: 'Green tax',
   city_tax: 'City tax',
   service_fee: 'Service fee',
   occupancy_tax: 'Occupancy tax',
};

export const RADIUS = 37000;
// RADIUS and TravelerSeasonHotelSearch.radius are in metres; use this to present them in miles.
export const METERS_PER_MILE = 1609.34;
export const RANGE_RADIUS = [70000];
export const MIN_NUMBER_HOTELS = 50;
export const NUMBER_EXCEEDS_HOTELS = 500;

export const INITIAL_ZOOM = 12;

export const MILES_SHOW_AMOUNT = 3.218;

export const URGENCY_THRESHOLD_HIGH = 5;
export const URGENCY_THRESHOLD_LOW = 9;

export const BOOKING_STATUS = {
   INIT: 'INIT',
   SUCCESS: 'SUCCESS',
   FAIL: 'FAIL',
   PAYMENT_PROCESSING: 'PAYMENT_PROCESSING',
   END_SESSION: 'END_SESSION',
};

export const SUPPORT_PHONE_NUMBER = '(800) 601-4678';

export const PRIVACY_POLICY_URL = 'https://legal.nonprofittrips.com/privacy-policy';
export const TERMS_OF_SERVICE_URL = 'https://legal.nonprofittrips.com/terms-of-service';

export const GENERAL_FUND = 'General Fund';


export const LAST_HOTEL_SEARCH = 'lastHotelSearch';
export const RECENT_SEARCHES_KEY = 'npt:recentSearches';
export const RECENT_SEARCHES_MIGRATED_KEY = 'npt:recentSearchesMigrated';
export const RECENT_SEARCHES_DEFAULT_LIMIT = 3;
export const RECENT_SEARCHES_MAX_LOCAL = 10;

export {
   acceptImageTypeFile,
   apiStatus,
   BUTTON_COLOR,
   defaultPageCount,
   delayShowToast,
   exportFileName,
   httpMethods,
   IMAGE_TYPE_REGEX,
   limit_default,
   ORGANIZATION_KEY,
   page_default,
   pageSizeOptions,
   pageType,
   permissionActions,
   permissionKeys,
   permissionsOrg,
   permissionsSite,
   roleNames,
   roles,
   roleTypeOptions,
   routePaths,
   searchMaxLength,
   status,
   TOKEN_KEY,
   USER_KEY,
   WORKOS_USER_KEY,
   RSVP_EVENT_KEY,
   DEFAULT_GIVE_BACK_PERCENT,
};

export const PROGRAM_LIMIT_DEFAULT = 100;

export const MAX_ADULTS_PER_ROOM = 6;
export const MAX_CHILDREN_PER_ROOM = 4;
// Mirrors the backend RetrieveHotelDto @Max(20) on roomNumber (BH's documented max).
export const MAX_ROOMS = 20;
// BH Search API criteria: totals across all rooms, and adults >= rooms.
export const MAX_TOTAL_ADULTS = 36;
export const MAX_TOTAL_CHILDREN = 35;

export const ROOM_FILTER_DEFAULT = { adults: 2, rooms: 1, children: 0 };

// The bed count behind the search page's "2+ beds" filter. Travelers shopping for two beds
// will take a room with more (two queens plus a sofa bed still sleeps them), so the filter is
// a floor rather than an exact match.
export const MIN_BEDS_FILTER = 2;
export const FAQS = [
   {
      id: uuidv4(),
      question: `How does this generate proceeds for <%= organizationName %> if you match the price of other sites?`,
      answer: `Travel sellers, like ourselves, get wholesale rates. The price you see and pay is the public rate. The difference between those two rates is the margin. Other sites take all of the margin for themselves. We split ours with your cause.`,
   },
   {
      id: uuidv4(),
      question: `I must be getting a worse service if you're making less per booking, right?`,
      answer: `Nope! Our partnership with your organization allows us to spend less on ads resulting in plenty of leftovers to share back. So you get the same service and redirect money towards the causes you support most!`,
   },
   {
      id: uuidv4(),
      question: `Can I book a hotel for any reason? Or does it have to be <%= organizationName %>-related?`,
      answer: `You can book for any reason! Family trips, business travel, weekend in the city, away tournament, weddings, college visits. If you need a hotel, book it here! We have over 3M hotels around the world including many top brands.`,
   },
   {
      id: uuidv4(),
      question: `How does <%= organizationName %> get the money?`,
      answer: `We send the check directly to them! When we do, you'll probably get a "thank you" from your organization's leadership!`,
   },
];

// NPT-1731 — COST_SAVING orgs pass their margin back to the traveler as a lower price;
// none of it goes to the org. The FAQS above claim proceeds/donations, which would be a
// false claim here, so this mirrors it 1-for-1 with savings-framed answers instead.
export const FAQS_COST_SAVING = [
   {
      id: uuidv4(),
      question: `How do you offer lower prices for <%= organizationName %> supporters if you match the price of other sites?`,
      answer: `Travel sellers, like ourselves, get wholesale rates. The price you see and pay is normally the public rate. The difference between those two rates is the margin. Other sites keep all of the margin for themselves. We pass ours back to you as a lower price instead.`,
   },
   {
      id: uuidv4(),
      question: `I must be getting a worse service if you're making less per booking, right?`,
      answer: `Nope! Our partnership with your organization allows us to spend less on ads, so we pass those savings straight to you. Same service, same hotels, just a lower price.`,
   },
   {
      id: uuidv4(),
      question: `Can I book a hotel for any reason? Or does it have to be <%= organizationName %>-related?`,
      answer: `You can book for any reason! Family trips, business travel, weekend in the city, away tournament, weddings, college visits. If you need a hotel, book it here! We have over 3M hotels around the world including many top brands.`,
   },
   {
      id: uuidv4(),
      question: `Does <%= organizationName %> get anything from my booking?`,
      answer: `<%= organizationName %> partnered with us so their supporters get lower prices on travel — the savings go straight to you, not to them.`,
   },
];

export const UploadType = {
   IMAGE: 'Image',
   LOGO: 'Logo',
   VIDEO: 'Video',
   IMAGE_VIDEO: 'Image/Video',
   CAPTIONS: 'Captions (.vtt)',
};

export const UploadFormatOfType = {
   [UploadType.IMAGE]: ['jpg', 'jpeg', 'png'],
   [UploadType.LOGO]: ['png', 'svg'],
   [UploadType.VIDEO]: ['mp4', 'webm'],
   [UploadType.IMAGE_VIDEO]: ['jpg', 'jpeg', 'png', 'mp4', 'webm'],
   [UploadType.CAPTIONS]: ['vtt'],
};
